import * as React from 'react';
import {
  Persona,
  PersonaSize,
  TooltipHost,
  ITooltipProps,
  DirectionalHint,
  TooltipDelay,
  DefaultButton,
  IPersonaProps,
  IPersonaSharedProps,
  IStyleFunctionOrObject,
  ITheme,
  styled,
  concatStyleSetsWithProps,
  PersonaCoin,
  Image,
  ImageFit,
  memoizeFunction,
  Icon,
  ITooltipHost
} from '@fluentui/react';
import {
  ILastEditDetails,
  PresenceColorProvider,
  TokenProvider,
  ITelemetryNamespaceLogger
} from '@fluidx/office-fluid-types';
import { getInitials } from '@uifabric/utilities';
import {
  getRelativeDateFromTimestamp,
  sendTelemetryEvent,
  Locale,
  LocaleDirection,
  getUIDirection
} from '@fluidx/utilities';
import { componentsIconName } from '@fluidx/icons';
import { CoauthorInformation } from './CoauthorGallery';
import { LastEditedUpdatesListener, LastEditedUpdater } from './LeapUpdater';
import {
  tooltipMaxWidth,
  LastEditedStyles,
  LastEditedStyleProps,
  getLastEditedDefaultStyle
} from './LastEdited.styles';
import { lastEditedTestId } from '../../utilities/testUtilities/testConstants';

export interface LastEditedProps {
  lastEditDetails: ILastEditDetails;
  presenceColorProvider: PresenceColorProvider;
  pageLogger?: ITelemetryNamespaceLogger;
  tokenProvider?: TokenProvider;

  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };
  lastEditedTooltipText: (placeholders: { authorName: string; relativeTimestamp: string }) => string;
  lastEditedAriaLabelText: (placeholders: { relativeTimestamp: string; authorName: string }) => string;
  relativeTimestampJustNow: string;
  relativeTimestampMinutesAgo: (placeholders: { minutes: number }) => string;
  relativeTimestampMinutesAgoFull: (placeholders: { minutes: number }) => string;
  relativeTimestampHoursAgo: (placeholders: { hours: number }) => string;
  relativeTimestampHoursAgoFull: (placeholders: { hours: number }) => string;
  relativeTimestampYesterday: (placeholders: { time: string }) => string;
  relativeTimestampWithinLastDays: (placeholders: { day: string; time: string }) => string;
  relativeTimestampJustNowLowerCase: string;
  relativeTimestampYesterdayLowerCase: (placeholders: { time: string }) => string;

  styles?: IStyleFunctionOrObject<LastEditedStyleProps, LastEditedStyles>;
  theme?: ITheme;
}

// This gives us some room in the future in case we decide to add more types to this.
type EnrichedIPersonaProps = IPersonaProps & React.AriaAttributes;

const oneMinute: number = 1000 * 60;

export const LastEdited = React.memo(
  styled(function LastEdited(props: LastEditedProps) {
    const {
      lastEditDetails,
      presenceColorProvider,
      pageLogger,
      tokenProvider,
      lastEditedTooltipText,
      lastEditedAriaLabelText,
      localization,
      styles,
      theme
    } = props;

    const stringTable = {
      relativeTimestampJustNow: props.relativeTimestampJustNow,
      relativeTimestampMinutesAgo: props.relativeTimestampMinutesAgo,
      relativeTimestampHoursAgo: props.relativeTimestampHoursAgo,
      relativeTimestampYesterday: props.relativeTimestampYesterday,
      relativeTimestampWithinLastDays: props.relativeTimestampWithinLastDays,
      relativeTimestampJustNowLowerCase: props.relativeTimestampJustNowLowerCase,
      relativeTimestampYesterdayLowerCase: props.relativeTimestampYesterdayLowerCase
    };

    const resolvedStyles = concatStyleSetsWithProps({ theme }, styles);

    const [authorInfo, setAuthorInfo] = React.useState<CoauthorInformation>();
    // Keeps track of the last edit details in order to be able to use this in the interval function.
    const lastEditDetailsRef = React.useRef<ILastEditDetails>(lastEditDetails);

    const tooltipHostRef = React.createRef<ITooltipHost>();

    // Keeps track of the mounted state of the control.
    let isMounted = false;

    // This effect's purpose is to keep track of the mounted state of the control.
    React.useEffect(() => {
      // The effect will be run for each render / mount so we know that the control is mounted.
      isMounted = true;
      // This callback is invoked when the control is unmounted, usually used to cleanup listeners, subscriptions etc.
      return () => {
        isMounted = false;
      };
    }, []);

    const lastEditedUpdatesListener = React.useCallback<LastEditedUpdatesListener>(
      (authorInfo: CoauthorInformation) => {
        // The listener callback is async so that means that we could potentially update the state
        // after the control is unmounted, if the promise is later resolved.
        // In order to fix this, we keep track of the state of the control and update the state only on the mounted control.
        if (isMounted) {
          setAuthorInfo(authorInfo);
        }
      },
      []
    );

    const getAriaLabel = (authorName: string): string => {
      const relativeTimestamp = getRelativeDateFromTimestamp(lastEditDetailsRef.current.timestamp, {
        stringTable: {
          ...stringTable,
          // Some narrators like jaws don't know to read '3m ago' or '3h ago' so we need to make sure the string doesn't contain any abbreviations.
          relativeTimestampMinutesAgo: props.relativeTimestampMinutesAgoFull,
          relativeTimestampHoursAgo: props.relativeTimestampHoursAgoFull
        },
        locale: localization.locale,
        // Since this will be used in the middle of a sentence, we need to use a localized string for that
        // instead of using toLowerCase.
        lowerCaseFirstLetter: true
      });
      return lastEditedAriaLabelText({ authorName, relativeTimestamp });
    };

    const getTooltipText = (authorName: string): string => {
      const relativeTimestamp = getRelativeDateFromTimestamp(lastEditDetailsRef.current.timestamp, {
        stringTable,
        locale: localization.locale,
        // Since this will be used in the middle of a sentence, we need to use a localized string for that
        // instead of using toLowerCase.
        lowerCaseFirstLetter: true
      });
      return lastEditedTooltipText({ authorName, relativeTimestamp });
    };

    const lastEditedUpdater = React.useRef<LastEditedUpdater>(
      new LastEditedUpdater(presenceColorProvider, lastEditedUpdatesListener, pageLogger, tokenProvider)
    );

    React.useEffect(() => {
      // If the token provider is not defined, log for debugging purposes
      if (pageLogger && !tokenProvider) {
        sendTelemetryEvent(pageLogger, { eventName: 'MissingTokenProvider' });
      }
    }, []);

    React.useEffect(() => {
      lastEditedUpdater.current.setPresenceColorProvider(presenceColorProvider);
    }, [presenceColorProvider]);

    // Make sure to update the UI accordingly based on how much time passed from the last edited.
    React.useEffect(() => {
      // tslint:disable-next-line no-string-based-set-interval
      const interval = setInterval(computeDataForRender, oneMinute);
      return () => {
        clearInterval(interval);
      };
    }, []);

    const getUserName = (): string => {
      // Try get the name from the stored IUser.
      const name = (lastEditDetails.user as any).name;
      // In case we don't have that information, will use the user id.
      return name || lastEditDetails.user.id;
    };

    const computeDataForRender = (): void => {
      const timestamp = lastEditDetailsRef.current.timestamp;
      const description = getRelativeDateFromTimestamp(timestamp, {
        stringTable,
        locale: localization.locale
      });

      // tslint:disable-next-line: no-floating-promises
      lastEditedUpdater.current.updateCoauthor({
        id: lastEditDetails.user.id,
        name: getUserName(),
        description,
        activeStatus: true,
        hasRing: true
      });
    };

    React.useEffect(() => {
      // Store this in order to be able to access it in the interval function.
      lastEditDetailsRef.current = lastEditDetails;
      computeDataForRender();
    }, [lastEditDetails]);

    /**
     * Returns a persona control combine the passed props with the default props.
     */
    const onRenderPersona = (props: EnrichedIPersonaProps): JSX.Element => {
      return (
        <Persona
          imageUrl={authorInfo?.imageUrl}
          size={PersonaSize.size32}
          initialsColor={authorInfo?.color}
          coinSize={20}
          onRenderPrimaryText={onRenderRelativeTimeSpan}
          showInitialsUntilImageLoads={true}
          onRenderInitials={onRenderPersonaInitials}
          // We need to set the role='presentation' on the underlying image of the PersonaCoin,
          // but fluent-ui doesn't expose an imageRole attribute so we need to create a custom render function
          // Opened an issue at fluent-ui to expose this attribute: https://github.com/microsoft/fluentui/issues/16400
          // TODO: When the issue is resolved, we can remove this param and rely on the default implementation.
          onRenderPersonaCoin={onRenderPersonaCoin}
          {...props}
        />
      );
    };

    const onRenderRelativeTimeSpan = (props?: IPersonaProps) => {
      return <span>{props?.text}</span>;
    };

    const onRenderPersonaImage = (props?: IPersonaSharedProps) => {
      // Render the Image component only if an image URL is provided
      if (!props?.imageUrl) {
        return null;
      }

      return (
        <Image
          imageFit={ImageFit.cover}
          src={props.imageUrl}
          width={props.coinSize}
          height={props.coinSize}
          alt={props.imageAlt}
          shouldFadeIn={props.imageShouldFadeIn}
          shouldStartVisible={props.imageShouldStartVisible}
          role={'presentation'}
          styles={resolvedStyles.subComponentStyles?.personaCoinImage}
          className={'ms-Persona-image'}
        />
      );
    };

    const onRenderPersonaInitials = () => {
      const isRtl = (localization.localeDirection || getUIDirection(localization.locale)) === 'rtl';
      const initials = getInitials(authorInfo?.name, isRtl);
      return initials ? <span>{initials}</span> : <Icon iconName={componentsIconName('FFXCPawn')} />;
    };

    const onRenderPersonaCoin = memoizeFunction((props?: IPersonaSharedProps) => {
      return (
        <PersonaCoin onRenderPersonaCoin={onRenderPersonaImage} onRenderInitials={onRenderPersonaInitials} {...props} />
      );
    });

    const onDismissTooltip = () => {
      tooltipHostRef.current?.dismiss();
    };

    const tooltipProps: ITooltipProps = {
      maxWidth: `${tooltipMaxWidth}px`,
      calloutProps: {
        isBeakVisible: false,
        // Apparently, if this is not specified, the Callout adds a container bridging the gap
        // So the beak is overlapping with the button (event if not visible) which makes hover unstable.
        // By explicitly setting the width to 0, we ensure that there's no overlapping and the hover works as expected.
        beakWidth: 0,
        gapSpace: 4,
        styles: {
          root: {
            borderRadius: 4
          }
        },
        onDismiss: onDismissTooltip
      },
      onRenderContent: authorInfo
        ? () =>
            onRenderPersona({
              text: getTooltipText(authorInfo.name),
              styles: resolvedStyles.subComponentStyles?.tooltipPersona
            })
        : undefined
    };

    return authorInfo ? (
      <TooltipHost
        tooltipProps={tooltipProps}
        delay={TooltipDelay.zero}
        directionalHint={DirectionalHint.bottomCenter}
        componentRef={tooltipHostRef}
      >
        <DefaultButton
          data-testid={lastEditedTestId}
          styles={resolvedStyles.subComponentStyles?.personaButton}
          ariaLabel={getAriaLabel(authorInfo.name)}
          role={'note'}
        >
          {onRenderPersona({
            text: authorInfo!.description,
            styles: resolvedStyles.subComponentStyles?.persona,
            // The persona control consists of 2 separate items, the image and the text.
            // Each of them is individually accessible, but we want to wrap the whole control into a single one.
            // For this reason, we will use the wrapper button for the screen readers and we will hide this control.
            // The button also allows us to handle the 'Space' and 'Enter' keys to show the tooltip.
            'aria-hidden': true
          })}
        </DefaultButton>
      </TooltipHost>
    ) : (
      <></>
    );
  }, getLastEditedDefaultStyle)
);

// cSpell:ignore testid fluentui uifabric ffxc
